// TODO(jon): This is a variation of the `AssigneeSelect` component in Tasks,
// and also similar to the `ConstituentAssigneeSelect` in ConstituentDetails. We
// will want to consolidate these three components, but that's probably best
// achieved after we've done some general `Select` refactoring.
import React from 'react';
import { useFormContext } from 'react-hook-form';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import ControlledSelect from '@/components/common/form/ControlledSelect';
import useUsers from '@/lib/hooks/swr/useUsers';
import useStatefulId from '@/lib/hooks/useStatefulId';
import translate from '@/lib/intl/translate';

export default function ControlledAssigneeSelect({
  className = '',
  disabled = false,
  displayEmpty,
  id = '',
  label = translate('CONSTITUENT_ASSIGNEE'),
  multiple,
  name = 'developers',
  required = false,
  shouldUnregister = false,
}: {
  className?: string;
  disabled?: boolean;
  displayEmpty?: boolean;
  id?: string;
  label?: string;
  multiple?: boolean;
  name?: string;
  required?: boolean;
  shouldUnregister?: boolean;
}) {
  const { control, watch } = useFormContext();
  const { data } = useUsers();
  const statefulId = useStatefulId(id);

  const watchedValue = watch(name);

  const sortedData = React.useMemo(
    () =>
      [...(data || [])].sort((a, b) =>
        a.displayName.localeCompare(b.displayName),
      ),
    [data],
  );

  const shrinkLabel =
    (multiple && !!watchedValue?.length) ||
    (!multiple && !!watchedValue) ||
    displayEmpty;

  return (
    <FormControl
      size="small"
      className={className}
      disabled={!data || disabled}
    >
      <InputLabel
        className="whitespace-nowrap"
        id={`label-${statefulId}`}
        required={required}
        shrink={shrinkLabel}
      >
        {label}
      </InputLabel>
      <ControlledSelect
        className="w-full"
        control={control}
        defaultValue={multiple ? [] : ''}
        displayEmpty={displayEmpty}
        id={statefulId}
        label={label}
        labelId={`label-${statefulId}`}
        multiple={multiple}
        name={name}
        notched={shrinkLabel}
        renderValue={(selected) => {
          if (multiple) {
            if (displayEmpty && !(selected as string[])?.length) {
              return translate('SHOW_ALL');
            }
            return sortedData
              .reduce<string[]>((prev, curr) => {
                if ((selected as string[]).includes(curr.userId)) {
                  return [...prev, `@${curr.displayName}`];
                }
                return prev;
              }, [])
              .join(', ');
          }
          return `@${sortedData.find((option) => selected === option.userId)
            ?.displayName}`;
        }}
        required={required}
        shouldUnregister={shouldUnregister}
      >
        {!required && !multiple && (
          <MenuItem value="">{translate('NONE')}</MenuItem>
        )}
        {sortedData.map(({ displayName, userId }) => (
          <MenuItem key={userId} value={userId}>
            {multiple && (
              <Checkbox
                checked={watchedValue.includes(userId)}
                className="py-2 pl-0"
              />
            )}
            {`@${displayName}`}
          </MenuItem>
        ))}
      </ControlledSelect>
    </FormControl>
  );
}
